import { useState, useCallback, useEffect, memo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { DgaIcon, DgaButton } from "platformscode-new-react";
const logo = "assets2/logos/hmm-logo-02.png";
import "./MobileNav.css";

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const xorEncrypt = (str, key = 66) =>
  btoa(str.split("").map((c) => String.fromCharCode(c.charCodeAt(0) ^ key)).join(""));

const isExternal = (href) => /^https?:\/\//.test(href ?? "");

const isActivePath = (href, pathname, search) => {
  if (!href || isExternal(href)) return false;
  const [linkPath, linkQuery] = href.split("?");
  if (pathname !== linkPath) return false;
  if (!linkQuery) return search === "" || search === "?";
  return (
    new URLSearchParams(linkQuery).get("category") ===
    new URLSearchParams(search).get("category")
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MENU DATA
// ─────────────────────────────────────────────────────────────────────────────
const MAIN_ITEMS = [
  { label: "الرئيسية", href: "/" },
  {
    label: "عن الأمانة",
    subItems: [
      { label: "عن الأمانة",                                            href: "/about" },
      { label: "أمين العاصمة المقدسة",                                  href: "/about-administrator" },
      { label: "أمناء العاصمة المقدسة",                                 href: "/amanah-in-sotor" },
      { label: "الهوية البصرية للأمانة",                                href: "/about/visual-identity" },
      { label: "وكلاء أمين العاصمة المقدسة",                           href: "/amanah-agents" },
      { label: "أمانة العاصمة المقدسة ورؤية المملكة 2030",             href: "/vision-2030" },
    ],
  },
  {
    label: "الخدمات الإلكترونية",
    subItems: [
      { label: "خدمات الأفراد",          href: `/electronic-services?category=${xorEncrypt("cit")}` },
      { label: "خدمات الأعمال",          href: `/electronic-services?category=${xorEncrypt("comp")}` },
      { label: "خدمات الجهات الحكومية", href: `/electronic-services?category=${xorEncrypt("gov")}` },
      { label: "بلدي",                   href: "https://balady.gov.sa/ar",        external: true },
      { label: "فرص",                    href: "https://furas.momah.gov.sa/",     external: true },
    ],
  },
  {
    label: "المركز الاعلامي",
    subItems: [
      { label: "أخبار الأمانة",   href: "/news" },
      { label: "فعاليات الأمانة", href: "/events" },
    ],
  },
  { label: "مركز المعرفة", href: "/knowledge-center" },
  { label: "تواصل معنا", href: "/contact" },
];

// ─────────────────────────────────────────────────────────────────────────────
// SUB-MENU  (CSS grid animation — zero JS height measurement)
// ─────────────────────────────────────────────────────────────────────────────
const SubMenu = memo(({ items, isOpen, submenuId, onNavigate, pathname, search }) => (
  <div
    id={submenuId}
    role="region"
    className={`mobile-nav__submenu${isOpen ? " mobile-nav__submenu--open" : ""}`}
    aria-hidden={!isOpen}
  >
    <div className="mobile-nav__submenu-inner">
      <ul className="mobile-nav__sublist" role="list">
        {items.map((sub, i) => {
          const active = isActivePath(sub.href, pathname, search);
          return (
            <li key={i}>
              {sub.external ? (
                <a
                  href={sub.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mobile-nav__subitem"
                  aria-label={`${sub.label} — يفتح في نافذة جديدة`}
                >
                  {sub.label}
                  <DgaIcon icon="link-square-02" size={12} variant="stroke" type="rounded" aria-hidden="true" />
                </a>
              ) : (
                <button
                  type="button"
                  className={`mobile-nav__subitem${active ? " mobile-nav__subitem--active" : ""}`}
                  onClick={() => onNavigate(sub.href)}
                  aria-current={active ? "page" : undefined}
                >
                  {sub.label}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  </div>
));

SubMenu.displayName = "SubMenu";

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
function MobileNavbar({ isOpen, closeMenu }) {
  const { pathname, search } = useLocation();
  const navigate             = useNavigate();
  const [openIndex, setOpenIndex] = useState(null);

  // Close submenus on route change
  useEffect(() => { setOpenIndex(null); }, [pathname, search]);

  // Escape key closes drawer
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === "Escape") closeMenu(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, closeMenu]);

  // Lock body scroll while drawer is open
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  // Close automatically when resized to desktop (≥900px)
  useEffect(() => {
    if (!isOpen) return;
    const mq = window.matchMedia("(min-width: 900px)");
    const handler = (e) => { if (e.matches) closeMenu(); };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [isOpen, closeMenu]);

  const navigateTo = useCallback((href) => {
    closeMenu();
    if (isExternal(href)) window.open(href, "_blank", "noopener,noreferrer");
    else navigate(href);
  }, [closeMenu, navigate]);

  const toggleSubmenu = useCallback((idx) => {
    setOpenIndex((prev) => (prev === idx ? null : idx));
  }, []);

  return (
    <nav
      id="mobile-nav"
      className={`mobile-nav${isOpen ? " mobile-nav--open" : ""}`}
      aria-label="قائمة التنقل للجوال"
      aria-hidden={!isOpen}
    >

     
      {/* ── Nav list ───────────────────────────────────────────────── */}
      <div className="mobile-nav__body">
        <ul className="mobile-nav__list" role="list">
          {MAIN_ITEMS.map((item, idx) => {
            const hasSubmenu = !!item.subItems?.length;
            const isExpanded = openIndex === idx;
            const active     = isActivePath(item.href, pathname, search);
            const submenuId  = `mobile-submenu-${idx}`;

            return (
              <li key={idx} className="mobile-nav__item">
                {hasSubmenu ? (
                  <>
                    <button
                      type="button"
                      className={`mobile-nav__trigger${isExpanded ? " mobile-nav__trigger--open" : ""}`}
                      aria-expanded={isExpanded}
                      aria-controls={submenuId}
                      onClick={() => toggleSubmenu(idx)}
                    >
                      {/* RTL: span (first) = RIGHT, chevron (last) = LEFT */}
                      <span>{item.label}</span>
                      <DgaIcon
                        icon="arrow-down-01"
                        variant="stroke"
                        type="rounded"
                        size={18}
                        aria-hidden="true"
                        className="mobile-nav__chevron"
                      />
                    </button>
                    <SubMenu
                      items={item.subItems}
                      isOpen={isExpanded}
                      submenuId={submenuId}
                      onNavigate={navigateTo}
                      pathname={pathname}
                      search={search}
                    />
                  </>
                ) : (
                  <button
                    type="button"
                    className={`mobile-nav__link${active ? " mobile-nav__link--active" : ""}`}
                    onClick={() => navigateTo(item.href)}
                    aria-current={active ? "page" : undefined}
                  >
                    <span>{item.label}</span>
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      {/* ── Footer bar ───────────────────────────────────────────────── */}
      <div className="mobile-nav__footer">
        {/* In RTL: first item = RIGHT, second = LEFT */}
        <div className="mobile-nav__footer-item">
          {/* Native button so long translated labels truncate with ellipsis */}
          <button
            type="button"
            className="mobile-nav__footer-native-btn"
            onClick={() => navigateTo(`/electronic-services?page=1&category=${xorEncrypt("emp")}`)}
          >
            <DgaIcon icon="user" size={24} variant="stroke" type="rounded" aria-hidden="true" />
            <span className="mobile-nav__footer-native-label">خدمات منسوبي الأمانة</span>
          </button>
        </div>
        <div id="translate-btn-mobile" className="mobile-nav__footer-item translate-btn">
          <DgaButton
            label="اللغة"
            variant="subtle"
            leadIcon
            leadIconType="translation"
            leadIconProps={{ size: 24, variant: "stroke", type: "rounded" }}
            size="lg"
            className="mobile-nav__footer-dga-btn"
          />
        </div>
      </div>

    </nav>
  );
}

export default MobileNavbar;
